import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import constants from 'helpers/constants';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import Button from 'components/atoms/Buttons/Button/Button';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import {Span} from 'components/atoms/Text/Typography/Typography';
import utils from 'helpers/utils';
import Icon from 'components/atoms/Icons/Icon/Icon';
import IosShare from '@mui/icons-material/IosShare';
import Close from '@mui/icons-material/Close';
import {useClientCallbacks} from 'services/client/client.utils';
import {useAuthTeamId} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import {useClientTeamMemberOptions} from 'services/client/team/team.utils';
import comments from 'helpers/comment';
import Avatar from 'components/atoms/Avatars/Avatar/Avatar';
import AvatarOption from 'components/molecules/Options/AvatarOption/AvatarOption';
import StyledShareDialog from 'components/organisms/Dialogs/ShareDialog/ShareDialog.styles';

const ShareDialog = React.forwardRef((props, ref) => {
  const {
    title,
    type,
    comment,
    onClose,
    onChange,
    onSubmit,
    ...innerProps
  } = useComponentProps({
    type: 'person',
    ...props
  }, 'ShareDialog');

  const innerRef = useRef(null);
  const formRef = useRef(null);
  const fieldRef = useRef(null);

  const [internalState, setInternalState] = useState({
    error: null,
    warning: null,
    dirty: null,
    validation: null,
    submitting: false,
    linkedin: null,
    email: null,
    params: null,
    lookup: null
  });

  useImperativeHandle(ref, () => innerRef.current);

  const teamId = useAuthTeamId();
  const teamMemberOptions = useClientTeamMemberOptions(teamId, true, false);
  const clientCallbacks = useClientCallbacks();

  const fields = useMemo(() => {
    const fields = [];

    fields.push({
      name: 'comment',
      label: 'Comment',
      entity: 'share',
      type: constants.formFieldTypes.richText,
      validation: constants.formFieldValidationTypes.text,
      required: true,
      initial: comment,
      FormFieldProps: {
        variant: 'staticLabel',
        minRows: 4,
        maxRows: 8,
        mentions: [{
          trigger: '@',
          data: (search, callback) => clientCallbacks.members({search, callback})
        }]
      },
    });

    fields.push({
      name: 'recipients',
      label: 'Recipients',
      placeholder: 'Type a username',
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.list,
      initial: [],
      filter: ['-0'],
      options: clientCallbacks.members,
      required: true,
      FormFieldProps: {
        ref: fieldRef,
        variant: 'staticLabel',
        multiple: true,
        fullWidth: true,
        ChipListProps: {
          variant: 'standard'
        },
        getTagProps: (option) => {
          const avatar = `${utils.personName(option.profile.firstName, option.profile.lastName)} (${option.profile.username})`;
          const color = utils.string2Color(avatar);
          return {
            icon: <Avatar color={color} size="tiny">{utils.avatarLabel(avatar)}</Avatar>
          };
        },
        renderOption: (option) => {
          const avatar = `${utils.personName(option.profile.firstName, option.profile.lastName)} (${option.profile.username})`;
          const name = utils.personName(option.profile.firstName, option.profile.lastName);

          return <AvatarOption avatar={avatar}
                               label={name}
                               size="smaller" />
        }
      }
    })

    return fields;
  }, [comment, clientCallbacks]);

  const handleChange = (field, value) => {
    onChange?.(field, value);

    setInternalState(utils.updater({
      error: null,
      validation: null
    }, true));
  };

  const handleChangeDirect = (e) => {
    const field = fields?.find((field) => field.name === e?.target?.name);
    const value = e?.target?.value;

    if (field.name === 'comment') {
      const mentions = utils.camelcaseEx(comments.commentMentions(value))
        .filter((m) => m.userId > 0)
        .map((m) => {
          return teamMemberOptions?.find((mb) => +mb.value === +m.userId);
        })
        .filter((_) => (_));

      fieldRef.current?.setValue(utils.uniqueArray((internalState.recipients ?? []).concat(
        mentions.filter((m1) => !(internalState.mentions ?? []).find((m2) => +m1.userId === +m2.userId))), 'value'));
      setInternalState(utils.updater({mentions: mentions}, true));
    } else if (field.name === 'recipients') {
      setInternalState(utils.updater({
        recipients: value,
        error: null,
        validation: null
      }, true));
    }
  }

  const handleSubmit = (values, actions) => {
    setInternalState(utils.updater({submitting: true}, true));

    utils.asPromise(onSubmit)(values)
      .then(() => {
        onClose?.(null, 'saveButtonClick');
      })
      .catch(() => {
        setInternalState(utils.updater({error: `Sharing ${type} failed`}, true));
      })
      .finally(() => {
        actions.setSubmitting(false);
        setInternalState(utils.updater({submitting: false}, true));
      });
  };

  const handleValidating = (isValidating, isDirty, hasErrors) => {
    setInternalState(utils.updater({
      dirty: isDirty,
      validation: hasErrors ? 'Please check if all fields have the correct values' : null
    }, true));
  }

  const handleSubmitClick = () => {
    formRef.current?.submit();
  };

  const handleCancel = (e) => {
    innerRef.current?.close?.(e);
  }

  const handleClose = (e, reason) => {
    if ((!internalState.submitting && !internalState.error && !internalState.dirty && !internalState.lookup) ||
        ['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      onClose?.(e, reason);
    }
  }

  const renderButtons = () => {
    return <React.Fragment>
      <Button children={'Cancel'}
              variant="text"
              startIcon={<Icon icon={Close}/>}
              onClick={handleCancel}/>
      <Button disabled={internalState.submitting}
              type="submit"
              variant="contained"
              color="success"
              children={`Share ${type}`}
              startIcon={<Icon icon={IosShare}/>}
              onClick={handleSubmitClick}/>
    </React.Fragment>
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledShareDialog ref={innerRef} {...innerProps} onClose={handleClose}>
    <DialogHeader title={title ?? `Share ${type}`} />
    <DialogContent>
      <InlineForm ref={formRef}
                  fields={fields}
                  onValidating={handleValidating}
                  onChange={handleChange}
                  onChangeDirect={handleChangeDirect}
                  onSubmit={handleSubmit} />
    </DialogContent>
    <DialogFooter className="ShareDialog-footer"
                  info={(internalState.error ?? internalState.validation) ?
                    <Span color="error">{internalState.error ?? internalState.validation}</Span> : null}
                  buttons={renderButtons()} />
  </StyledShareDialog>
});

ShareDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  title: PropTypes.string,
  comment: PropTypes.string,
  type: PropTypes.oneOfType([PropTypes.oneOf(['view']), PropTypes.string]),
  country: PropTypes.string,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func
};

export default ShareDialog;
